import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { Spacing } from "@glossy";
import { tr } from "@linkfluence/i18n";
import { DateMath, Range } from "@linkfluence/models/date";
import { Interval } from "@linkfluence/models/date";
import { Locale } from "@linkfluence/models/locale";
import { Timezone } from "@linkfluence/models/timezone";
import { getRangeFromDates } from "@tootools/date";
import Button from "@ui-kit/button";
import Calendar from "@ui-kit/period-selector/calendar";
import { getPeriodFromDates, isApplyButtonDisabled } from "../data";

type Period = Omit<Range, "interval">;
interface IProps {
  limits: Period;
  period: Period;
  customHour?: boolean;
  locale: Locale;
  timezone: Timezone;
  onChange: (period: Period) => void;
  onClose?: () => void;
  allowCancel?: boolean;
  allowFromOnly?: boolean;
  isNewCalendar?: boolean;
  allowBeyondLimit?: boolean;
  limitTo100Days?: boolean;
}

const Root = styled.div<{ allowFromOnly: boolean }>`
  width: ${({ allowFromOnly }) => (allowFromOnly ? "auto" : "444px")};
`;

const StyledCalendar = styled(Calendar)<{ allowFromOnly?: boolean }>`
  width: ${({ allowFromOnly }) => (allowFromOnly ? "auto" : "210px")};
  &:first-child {
    margin-right: ${({ allowFromOnly }) => (allowFromOnly ? "auto" : "24px")};
  }
`;

const Calendars = styled.div`
  display: flex;
  justify-content: space-between;
  box-sizing: border-box;
`;

const Footer = styled.div`
  padding-top: 24px;
  text-align: center;
  display: flex;
  justify-content: center;
  column-gap: ${Spacing.Small};
`;

const useRange = (
  period: Period,
  timezone: Timezone,
  limits: Period,
  isNewCalendar: boolean,
  allowFromOnly: boolean,
  onChange: (period: Period) => void,
  limitTo100Days?: boolean
) => {
  const _period = useMemo(
    () => getPeriodFromDates(period.from, period.to, timezone),
    [period, timezone]
  ) as Period;
  const [currentPeriod, setCurrentPeriod] = useState(_period);
  const _limits = useMemo(
    () =>
      getRangeFromDates(
        limits.from,
        limits.to,
        timezone,
        undefined,
        currentPeriod.to,
        limitTo100Days
      ),
    [currentPeriod.to, limitTo100Days, limits.from, limits.to, timezone]
  );
  const isDisabled = useMemo(() => {
    return isApplyButtonDisabled(
      isNewCalendar,
      allowFromOnly,
      { prev: _period.from, next: currentPeriod.from },
      { prev: _period.to, next: currentPeriod.to }
    );
  }, [_period, allowFromOnly, currentPeriod, isNewCalendar]);

  const handleChange = () => {
    if (allowFromOnly) {
      return onChange({
        from: new Date(currentPeriod.from.toString()).toISOString(),
        to: DateMath.Now,
      });
    }
    return onChange(currentPeriod);
  };

  return { _limits, currentPeriod, setCurrentPeriod, handleChange, isDisabled };
};

const UiKitPeriodSelectorRange = ({
  limits,
  period,
  customHour,
  locale,
  timezone,
  onChange,
  onClose,
  allowCancel = true,
  allowFromOnly = false,
  isNewCalendar = true,
  allowBeyondLimit = false,
  limitTo100Days,
}: IProps) => {
  const { _limits, currentPeriod, setCurrentPeriod, handleChange, isDisabled } =
    useRange(
      period,
      timezone,
      limits,
      isNewCalendar,
      allowFromOnly,
      onChange,
      limitTo100Days
    );
  return (
    <Root allowFromOnly={allowFromOnly}>
      <Calendars>
        <StyledCalendar
          allowFromOnly={allowFromOnly}
          limits={_limits}
          period={currentPeriod}
          interval={Interval.Day}
          customHour={customHour}
          type="start"
          locale={locale}
          timezone={timezone}
          onChange={setCurrentPeriod}
          allowBeyondLimit={allowBeyondLimit}
        />
        {!allowFromOnly && (
          <StyledCalendar
            limits={_limits}
            period={currentPeriod}
            interval={Interval.Day}
            customHour={customHour}
            type="end"
            locale={locale}
            timezone={timezone}
            onChange={setCurrentPeriod}
            allowBeyondLimit={allowBeyondLimit}
          />
        )}
      </Calendars>
      <Footer>
        <Button
          text={tr(locale, "CALENDAR_PICKER::BUTTONS::APPLY")}
          onClick={handleChange}
          size="m"
          variant="primary"
          isDisabled={isDisabled}
        />
        {allowCancel && (
          <Button
            text={tr(locale, "CALENDAR_PICKER::BUTTONS::CANCEL")}
            onClick={onClose}
            size="m"
            variant="tertiary"
          />
        )}
      </Footer>
    </Root>
  );
};

export default UiKitPeriodSelectorRange;
